import React, { useState, useEffect } from "react";
import { useMutation } from "react-apollo";
import {
  Button,
  Box,
  Flex,
  Stack,
  Text,
  StatNumber,
  Link,
  useDisclosure,
  Divider,
} from "@chakra-ui/core";
import { FaLink } from "react-icons/fa";
// import { GoGraph } from "react-icons/go";
import {
  MdSettings,
  MdAccountBalanceWallet,
  MdOpenInNew,
} from "react-icons/md";
import { Link as ReachLink } from "react-router-dom";
import NewPaymentLink from "../forms/NewPaymentLink";
import { CREATE_PAYOUT } from "../../graphql/mutations";
import PayOutModal from "../elements/PayOutModal";
import getFormattedAmount from "../../helpers/formatAmount";

/** The section right below the dashboard header
 * This section contain high level info and actions such as
 * Week performance and connection to Stripe account
 */

const DashboardLeadingSection = ({
  businessName,
  balance,
  stripeAccountId,
  transactions,
  refetch
}) => {
  const [isCreateLink, setIsCreateLink] = React.useState(false);
  const [createPayout] = useMutation(CREATE_PAYOUT);
  const [status, setStatus] = useState("");
  const [isPayoutError, setIsPayoutError] = useState(false);
  const [isProcessingPayout, setIsProcessingPayout] = useState(false);
  const [pendingPayments, setPendingPayments] = useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();

  let { available, pending } = balance;

  let actualBalance = available;

  pending = getFormattedAmount(pending);
  available = getFormattedAmount(available);

  const handlePayout = () => {
    if (!actualBalance) {
      setStatus("Sorry, you don't have sufficient balance available at the moment");
      setIsPayoutError(true)
      onOpen();
    }
    else {
      onOpen();
      setIsProcessingPayout(true)
      setStatus(`Processing your payout request`);
      createPayout()
      .then(() => {
        setStatus(`Congratulation, we paid out ${available} to your bank account`);
        setIsProcessingPayout(false);
        refetch();
      })
      .catch((err) => {
        const errorStack = err.message.split(":");
        console.log(errorStack[errorStack.length - 1]);
        setStatus("Something went wrong, please try again later")
        setIsPayoutError(true)
        setIsProcessingPayout(false)
      });
    }
  };

  const reinitialiseState = () => {
    setStatus("");
    setIsPayoutError(false);
    setIsProcessingPayout(false)
  }

  /**
   * this side effect will calculate the
   * pending payments from customers and
   * render the total in the financial info
   * (Leading section of the dashboard)
   */
  useEffect(()=>{
    let totalPendingPayments = 0;
    if(transactions){
      transactions.forEach((transaction) => {
        if(transaction.status === "pending") {
          totalPendingPayments += Number(transaction.amount);
        }
      });
    }
    setPendingPayments(getFormattedAmount(totalPendingPayments))
  },[transactions])

  return (
    <>
      <Box width="100%" py="15px" bg="primary.600" p={0}>
        <Flex align="center" justify="center" w="100%">
          <Box px={8} maxWidth="1280px" w="100%" h="100%">
            <Flex
              flexWrap="wrap"
              size="100%"
              align="center"
              justifyContent="space-between"
            >
              <Stack alignItems="flex-start" minW="300px" mx="22px">
                <Stack spacing={0}>
                  <Stack alignItems="center" isInline>
                    <Text color="primary.100" ontWeight="bold" fontSize="2xl">
                      {businessName} <br />
                    </Text>
                    <Link as={ReachLink} to="/account/setting">
                      <Box color="primary.100" size="20px" as={MdSettings} />
                    </Link>
                  </Stack>
                  <a
                    style={{ color: "#15A545" }}
                    href={`https://dashboard.stripe.com/b/${stripeAccountId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Stack isInline alignItems="center">
                      <Text>Your Stripe account</Text>
                      <Box as={MdOpenInNew} />
                    </Stack>
                  </a>
                </Stack>

                <Stack
                p="16px"
                bg="primary.700"
                borderRadius="5px"
                  my="16px"
                  flexWrap="wrap"
                  color="primary.300"
                  spacing={8}
                  isInline
                  w="100%"
                >
                  <Stack justifyContent="center">
                      <Text color="primary.100" fontSize="xs">
                        Available Balance
                      </Text>
                    <StatNumber color="success.100" fontSize="36px">{available}</StatNumber>
                  </Stack>
                  <Divider orientation="vertical" height="60px" borderColor="primary.500" />
                  <Stack justifyContent="flex-start">
                      <Text color="primary.100" fontSize="xs">
                        Soon available
                      </Text>
                    <StatNumber color="warning.300" fontSize="24px">{pending}</StatNumber>
                  </Stack>
                  <Divider orientation="vertical" height="60px" borderColor="primary.500" />
                  <Stack justifyContent="flex-start">
                      <Text color="primary.100" fontSize="xs">
                        Outstanding customers payments
                      </Text>
                    <StatNumber color="danger.300" fontSize="24px">{pendingPayments}</StatNumber>
                  </Stack>
                </Stack>
              </Stack>

              <Stack m="16px" isInline spacing={2} align="center">
                <Button
                  size="sm"
                  cursor="pointer"
                  bg="primary.500"
                  _focus={{ outline: "none" }}
                  _hover={{
                    bg: "primary.700",
                  }}
                  color="primary.100"
                  leftIcon={MdAccountBalanceWallet}
                  onClick={handlePayout}
                >
                  Payout now
                </Button>
                <Button
                  size="sm"
                  cursor="pointer"
                  bg="success.600"
                  _focus={{ outline: "none" }}
                  _hover={{
                    bg: "success.700",
                  }}
                  color="primary.100"
                  leftIcon={FaLink}
                  onClick={() => setIsCreateLink(true)}
                >
                  New payment link
                </Button>
              </Stack>
            </Flex>
          </Box>
        </Flex>
      </Box>
      {/** Form for new payment link */}
      <NewPaymentLink {...{ isCreateLink, setIsCreateLink, refetch }} />
      {/* Modal to display Payout status */}
      <PayOutModal {...{ status, isPayoutError, isProcessingPayout, reinitialiseState, isOpen, onClose }} />
    </>
  );
};

export default DashboardLeadingSection;
